import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Grid,
  CircularProgress,
  Tooltip,
  IconButton,
  useTheme,
  Alert,
  Chip,
} from '@mui/material';
import {
  ReceiptLong as SalesIcon,
  ShoppingCart as PurchasesIcon,
  AccountBalance as ProfitIcon,
  InfoOutlined as InfoIcon,
  Refresh as RefreshIcon,
  WifiOff as OfflineIcon,
  Payments as PayablesIcon,
  AccountBalanceWallet as ReceivablesIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import indexedDBService from '../../utils/indexedDBService';
import { MetricCard } from '../common/AnalyticsCard';

interface MonthlyData {
  currentMonth: number;
  previousMonth: number;
  percentageChange: number;
}

const CurrentMonthInsightsWidget: React.FC = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // True ERP Metrics
  const [grossRevenue, setGrossRevenue] = useState<MonthlyData>({ currentMonth: 0, previousMonth: 0, percentageChange: 0 });
  const [netProfit, setNetProfit] = useState<MonthlyData>({ currentMonth: 0, previousMonth: 0, percentageChange: 0 });
  const [totalReceivables, setTotalReceivables] = useState<number>(0);
  const [totalPayables, setTotalPayables] = useState<number>(0);

  const now = new Date();
  const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(now);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (currentUser) fetchData();
  }, [currentUser]);

  const calculatePercentageChange = (current: number, previous: number): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const getTransactionDate = (date: any): Date => {
    if (!date) return new Date();
    if (date instanceof Date) return date;
    return new Date(date);
  };

  const fetchData = async () => {
    if (!currentUser) return;
    setLoading(true);
    setErrorMessage(null);

    try {
      const curMonth = now.getMonth();
      const curYear = now.getFullYear();
      const curMonthStart = new Date(curYear, curMonth, 1);
      
      const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
      const prevYear = curMonth === 0 ? curYear - 1 : curYear;
      const prevMonthStart = new Date(prevYear, prevMonth, 1);
      const prevMonthEnd = new Date(curYear, curMonth, 0);

      // ── Load Data Stores ──
      const [invoices, posTransactions, purchases, expenses] = await Promise.all([
        indexedDBService.getAllItems<any>('invoices'),
        indexedDBService.getAllItems<any>('pos_transactions').catch(() => []), // gracefully fail if store doesn't exist
        indexedDBService.getAllItems<any>('purchases'),
        indexedDBService.getAllItems<any>('expenses')
      ]);

      const uid = currentUser.uid;
      const userInvoices = invoices.filter(i => i.userId === uid);
      const userPos = posTransactions.filter(p => p.userId === uid);
      const userPurchases = purchases.filter(p => p.userId === uid);
      const userExpenses = expenses.filter(e => e.userId === uid);

      // ── Total Receivables ──
      // Sum of partial or outstanding invoice balances
      const openReceivables = userInvoices.reduce((acc, inv) => {
        if (inv.status?.toLowerCase() !== 'paid') {
          // Usually 'balanceDue' or 'totalAmount' - 'amountPaid'
          const due = parseFloat(inv.balanceDue || inv.totalAmount || 0);
          return acc + due;
        }
        return acc;
      }, 0);
      setTotalReceivables(openReceivables);

      // ── Total Payables ──
      const openPayables = userPurchases.reduce((acc, p) => {
        if (p.status?.toLowerCase() === 'pending' || p.status?.toLowerCase() === 'partially paid') {
          const due = parseFloat(p.balanceDue || p.totalAmount || 0);
          return acc + due;
        }
        return acc;
      }, 0);
      setTotalPayables(openPayables);

      // ── Financial Analytics Accumulators ──
      let curSales = 0, prevSales = 0;
      let curPurchases = 0, prevPurchases = 0;
      let curExpenses = 0, prevExpenses = 0;

      // Process Invoices & POS for Revenue
      const processRevenueEntry = (dateRaw: any, amountRaw: any) => {
        const date = getTransactionDate(dateRaw);
        const amt = parseFloat(amountRaw || 0);
        if (date >= curMonthStart) curSales += amt;
        else if (date >= prevMonthStart && date <= prevMonthEnd) prevSales += amt;
      };

      userInvoices.forEach(inv => processRevenueEntry(inv.date || inv.invoiceDate, inv.totalAmount || inv.amount));
      userPos.forEach(pos => processRevenueEntry(pos.date || pos.createdAt, pos.total || pos.amount));

      // Process Purchases
      userPurchases.forEach(p => {
        const date = getTransactionDate(p.date);
        const amt = parseFloat(p.totalAmount || p.amount || 0);
        if (date >= curMonthStart) curPurchases += amt;
        else if (date >= prevMonthStart && date <= prevMonthEnd) prevPurchases += amt;
      });

      // Process Expenses
      userExpenses.forEach(e => {
        const date = getTransactionDate(e.date);
        const amt = parseFloat(e.amount || 0);
        if (date >= curMonthStart) curExpenses += amt;
        else if (date >= prevMonthStart && date <= prevMonthEnd) prevExpenses += amt;
      });

      // ── Set Calculated Final Stats ──
      setGrossRevenue({
        currentMonth: curSales,
        previousMonth: prevSales,
        percentageChange: calculatePercentageChange(curSales, prevSales)
      });

      const curProfit = curSales - curPurchases - curExpenses;
      const prevProfit = prevSales - prevPurchases - prevExpenses;
      setNetProfit({
        currentMonth: curProfit,
        previousMonth: prevProfit,
        percentageChange: calculatePercentageChange(curProfit, prevProfit)
      });

    } catch (error) {
      console.error('Failed to update analytics:', error);
      setErrorMessage('Failed to calculate updated analytics metrics.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading && !refreshing) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box>
      {errorMessage && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {monthName} Insights
          </Typography>
          <Tooltip title="Real-time financial performance KPIs">
            <IconButton size="small" sx={{ ml: 0.5 }}><InfoIcon fontSize="small" /></IconButton>
          </Tooltip>
          <IconButton onClick={handleRefresh} disabled={refreshing} size="small" color="primary" sx={{ ml: 0.5 }}>
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Box>
        {isOffline && <Chip icon={<OfflineIcon />} label="Offline" size="small" color="warning" variant="outlined" />}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Gross Revenue"
            value={grossRevenue.currentMonth}
            percentageChange={grossRevenue.percentageChange}
            icon={<SalesIcon />}
            color="#2563eb"
            linkTo="/invoices"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Net Profit"
            value={netProfit.currentMonth}
            percentageChange={netProfit.percentageChange}
            icon={<ProfitIcon />}
            color="#059669"
            linkTo="/sales-insights"
            delay={0.1}
          />
        </Grid>
        {/* Standard static metrics for current total exposure */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Receivables"
            value={totalReceivables}
            percentageChange={0} // Just showing absolute exposure value
            icon={<ReceivablesIcon />}
            color="#ca8a04"
            linkTo="/receivables"
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Payables"
            value={totalPayables}
            percentageChange={0}
            icon={<PayablesIcon />}
            color="#dc2626"
            linkTo="/accounts-payable"
            delay={0.3}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default CurrentMonthInsightsWidget;
